import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Press } from '@dorixona/ui'
import { BackButton } from '../../ui/BackButton'
import { Icon } from '../../ui/Icon'
import { Loading } from '../../ui/Load'
import { useSession } from '../../app/session'
import { useMapsOn } from '../../app/settings'
import { useAddresses } from '../account/api'
import type { DeliveryAddress } from './api'
import { AddressFinder } from './AddressFinder'
import { useAddressDraft } from './addressDraft'
import { chooseAddress, useChosenAddress } from './chosenAddress'
import { addressTitle, locatedAddresses } from './model'

const same = (a: DeliveryAddress, b: DeliveryAddress | null) =>
  b !== null && a.label === b.label && a.lat === b.lat && a.lng === b.lng

/**
 * Адрес доставки. Есть сохранённые — сначала они, с уже отмеченным основным
 * (или тем, что выбрали раньше): обычно везут туда же. Другой адрес — по
 * кнопке: поиск с подсказками геокодера и карта, на которой точку ставят
 * нажатием. Выбор уходит в оформление — там по нему сервер ищет ближайшую
 * аптеку и считает курьера.
 */
export function AddressScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation(['checkout', 'common'])
  const { isAuthenticated } = useSession()
  const chosen = useChosenAddress()
  const addresses = useAddresses(isAuthenticated)

  function confirm(address: DeliveryAddress) {
    chooseAddress(address)
    // Пришли с оформления — возвращаемся туда же, не плодя записей истории.
    if (location.key === 'default') void navigate('/checkout', { replace: true })
    else void navigate(-1)
  }

  return (
    <section className="screen checkout-wrap addr-wrap">
      <div className="mhead">
        <BackButton to="/checkout" />
        <h1>{t('checkout:addressPage.title')}</h1>
      </div>

      {/* Ждём список: иначе сначала мелькнёт карта, а потом её сменят адреса. */}
      {isAuthenticated && addresses.isPending ? (
        <Loading what={t('common:state.loading')} count={2} />
      ) : (
        <AddressChoice
          saved={locatedAddresses(addresses.data ?? [])}
          chosen={chosen}
          onConfirm={confirm}
        />
      )}
    </section>
  )
}

interface AddressChoiceProps {
  /** Сохранённые с точкой, основной первым. */
  saved: DeliveryAddress[]
  chosen: DeliveryAddress | null
  onConfirm: (address: DeliveryAddress) => void
}

function AddressChoice({ saved, chosen, onConfirm }: AddressChoiceProps) {
  const { t } = useTranslation(['checkout', 'common'])
  // Раньше выбрали адрес не из списка — открываем сразу поиск с ним.
  const chosenIsSaved = chosen !== null && saved.some((address) => same(address, chosen))
  const [mode, setMode] = useState<'saved' | 'other'>(
    saved.length > 0 && (chosen === null || chosenIsSaved) ? 'saved' : 'other',
  )
  const [pick, setPick] = useState<DeliveryAddress | null>(
    chosenIsSaved ? chosen : (saved[0] ?? null),
  )
  const draft = useAddressDraft(chosenIsSaved ? null : chosen)
  // Карты выключены в админке — новый адрес не указать: есть сохранённые —
  // выбирают из них, и «Другой адрес» не предлагаем.
  const mapsOff = useMapsOn() === false
  const view = mapsOff && saved.length > 0 ? 'saved' : mode
  const selected = view === 'saved' ? pick : draft.selected

  return (
    <>
      {view === 'saved' ? (
        <div className="sect">
          <div className="sect-head">
            <h2>{t('checkout:addressPage.saved')}</h2>
          </div>
          <div className="card" role="radiogroup" aria-label={t('checkout:addressPage.saved')}>
            {saved.map((address) => (
              <label className="opt" key={address.label}>
                <input
                  type="radio"
                  name="address"
                  value={address.label}
                  checked={same(address, pick)}
                  onChange={() => {
                    setPick(address)
                  }}
                />
                <div>
                  <div className="t1">{addressTitle(address)}</div>
                  {address.tag ? <div className="t2">{address.label}</div> : null}
                </div>
              </label>
            ))}
          </div>
          {mapsOff ? null : (
            <div className="card addr-switch">
              <Press
                squish="row"
                className="opt addr-row"
                onPress={() => {
                  setMode('other')
                }}
              >
                <Icon name="i-pin" size={18} />
                <div>
                  <div className="t1">{t('checkout:addressPage.other')}</div>
                  <div className="t2">{t('checkout:addressPage.otherHint')}</div>
                </div>
                <Icon name="i-chev" size={16} />
              </Press>
            </div>
          )}
        </div>
      ) : (
        <>
          <AddressFinder draft={draft} />
          {saved.length > 0 ? (
            <div className="card addr-switch">
              <Press
                squish="row"
                className="opt addr-row"
                onPress={() => {
                  setMode('saved')
                }}
              >
                <Icon name="i-home" size={18} />
                <div className="t1">{t('checkout:addressPage.fromSaved')}</div>
                <Icon name="i-chev" size={16} />
              </Press>
            </div>
          ) : null}
        </>
      )}

      <div className="addr-confirm">
        <Button
          disabled={!selected}
          onPress={() => {
            // Кнопка заперта, пока адреса нет: сюда попадаем только с ним.
            onConfirm(selected as DeliveryAddress)
          }}
        >
          {t('checkout:addressPage.confirm')}
        </Button>
      </div>
    </>
  )
}
